/**
 * Clara Valuation Intelligence - Property Valuation Page (FIXED VERSION)
 * 
 * FIXES APPLIED:
 * - Enhanced error handling and logging
 * - Validation of result object before state updates
 * - Better user feedback for errors
 * - Fallback rendering for missing data
 * - Console logging at each step for debugging
 * 
 * @version 2.0.0 (FIXED)
 * @author Manus AI
 * @date 2025-10-13
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, TrendingUp, Target, BarChart3, CheckCircle, 
  AlertTriangle, AlertCircle, Info, Save, RefreshCw, 
  Building, MapPin, Calendar, Ruler, Home
} from 'lucide-react';
import valuationService from '../services/valuationService';
import { masterSupabase as supabase } from '../lib/supabase-master';
import { CalculationTransparency } from '../components/CalculationTransparency';

import { MARKET_COMPARISON_RELEASED } from '../lib/marketComparisonGate';

export default function PropertyValuation() {
  const navigate = useNavigate();
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  
  // Property selection
  const [selectionMode, setSelectionMode] = useState('existing'); // 'existing' or 'new'
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [newPropertyData, setNewPropertyData] = useState({
    address: '',
    city: '',
    postal_code: '',
    size: '',
    build_year: '',
    property_type: 'MFH'
  });
  
  // Valuation results
  const [valuationResult, setValuationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Comparable picker state
  const [comparables, setComparables] = useState([]);
  const [comparableWeights, setComparableWeights] = useState({});
  
  // Tenants data
  const [tenants, setTenants] = useState([]);

  // Load properties on mount
  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .order('property_name');
      
      if (error) throw error;
      setProperties(data || []);
    } catch (err) {
      console.error('Error loading properties:', err);
    }
  };

  const loadComparables = async () => {
    try {
      const { data, error } = await supabase
        .from('property_suggestions')
        .select('*')
        .limit(10);
      
      if (error) throw error;
      
      setComparables(data || []);
      
      // Initialize weights to 100% for all
      const initialWeights = {};
      (data || []).forEach(comp => {
        initialWeights[comp.id] = 100;
      });
      setComparableWeights(initialWeights);
    } catch (err) {
      console.error('Error loading comparables:', err);
    }
  };

  const loadTenants = async (propertyId) => {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('property_id', propertyId);
      
      if (error) throw error;
      setTenants(data || []);
    } catch (err) {
      console.error('Error loading tenants:', err);
    }
  };

  const handlePropertySelect = async (propertyId) => {
    console.log('=== handlePropertySelect called ===');
    console.log('propertyId:', propertyId);
    console.log('properties array:', properties);
    console.log('properties length:', properties.length);
    
    if (!propertyId || propertyId === '') {
      console.log('Empty propertyId, clearing selection');
      setSelectedProperty(null);
      return;
    }
    
    const property = properties.find(p => p.id === propertyId);
    console.log('Found property:', property);
    
    if (!property) {
      console.error('Property not found in properties array!');
      console.error('Looking for ID:', propertyId);
      console.error('Available IDs:', properties.map(p => p.id));
      alert(`DEBUG: Property not found! ID: ${propertyId}, Properties count: ${properties.length}`);
      return;
    }
    
    setSelectedProperty(property);
    console.log('Selected property set:', property.property_name || property.address);
    await loadTenants(propertyId);
  };

  const handleCalculateValuation = async () => {
    console.log('=== STARTING VALUATION CALCULATION ===');
    setLoading(true);
    setError(null);
    
    try {
      // Determine property to value
      let propertyToValue = selectionMode === 'existing' 
        ? selectedProperty 
        : newPropertyData;
      
      // WORKAROUND: If selectedProperty is null but dropdown has a value, find the property manually
      if (!propertyToValue && selectionMode === 'existing') {
        const selectElement = document.querySelector('select');
        if (selectElement && selectElement.value) {
          const propertyId = selectElement.value;
          propertyToValue = properties.find(p => p.id === propertyId);
          console.log('Workaround: Found property from dropdown:', propertyToValue);
        }
      }
      
      console.log('Property to value:', propertyToValue);
      
      if (!propertyToValue) {
        throw new Error('Bitte wählen Sie ein Objekt aus oder geben Sie die Daten ein');
      }
      
      // Load comparables and wait for the result
      let comparablesToUse = comparables;
      let weightsToUse = comparableWeights;
      
      if (comparables.length === 0) {
        console.log('Loading comparables from database...');
        try {
          const { data, error } = await supabase
            .from('property_suggestions')
            .select('*')
            .limit(10);
          
          if (!error && data) {
            comparablesToUse = data;
            
            // Initialize weights
            const initialWeights = {};
            data.forEach(comp => {
              initialWeights[comp.id] = 100;
            });
            weightsToUse = initialWeights;
            
            // Update state for UI
            setComparables(data);
            setComparableWeights(initialWeights);
            console.log('Loaded comparables:', data.length);
          }
        } catch (err) {
          console.warn('Could not load comparables:', err);
          comparablesToUse = [];
        }
      }
      
      // INTERNAL-COMPARABLE-ADAPTER-1: interne Faktenquelle (read-only)
      let internalRowsToUse = [];
      let rentLayerToUse = null;
      try {
        const { data: internalData, error: internalError } = await supabase
          .from('properties')
          .select('id,property_name,street,city,postal_code,property_type,purchase_price,purchase_date,total_square_meters,rentable_area,total_area,ground_area,construction_year,year_built,estimated_market_value,monthly_rent_total,created_at,updated_at')
          .limit(200);
        if (!internalError && internalData) internalRowsToUse = internalData;
        const unitCount = await supabase.from('units').select('id', { count: 'exact', head: true });
        const tenantCount = await supabase.from('tenants').select('id', { count: 'exact', head: true });
        const rentCount = await supabase.from('rent_payments').select('id', { count: 'exact', head: true });
        rentLayerToUse = {
          units: unitCount.count || 0,
          tenants: tenantCount.count || 0,
          rent_payments: rentCount.count || 0
        };
      } catch (err) {
        console.warn('Internal fact source not available:', err);
      }
      console.log('Calling calculateFairValueBand with:', {
        property: propertyToValue,
        tenants: tenants.length,
        comparables: comparablesToUse.length
      });
      
      // Calculate valuation with loaded comparables
      const result = await valuationService.calculateFairValueBand(
        propertyToValue,
        tenants,
        comparablesToUse,
        {
          weights: weightsToUse,
          internalRows: internalRowsToUse,
          rentLayer: rentLayerToUse
        }
      );
      
      console.log('=== VALUATION RESULT RECEIVED ===');
      console.log('Result:', JSON.stringify(result, null, 2));
      
      // CRITICAL FIX: Validate result object before setting state
      if (!result) {
        throw new Error('Bewertung lieferte kein Ergebnis');
      }
      
      // Check if result contains an error
      if (result.error || !result.success) {
        const errorMessage = result.error || 'Bewertung fehlgeschlagen';
        const details = result.errors ? result.errors.join(', ') : '';
        throw new Error(`${errorMessage}${details ? ': ' + details : ''}`);
      }
      
      // Validate fair_value_band exists
      if (!result.fair_value_band) {
        console.error('Result missing fair_value_band:', result);
        throw new Error('Bewertungsergebnis ist unvollständig (fair_value_band fehlt)');
      }
      
      // Validate fair_value_band structure
      const { conservative, base, aggressive } = result.fair_value_band;
      if (typeof conservative !== 'number' || typeof base !== 'number' || typeof aggressive !== 'number') {
        console.error('Invalid fair_value_band values:', result.fair_value_band);
        throw new Error('Bewertungsergebnis enthält ungültige Werte');
      }
      
      console.log('✓ Validation passed, setting result state...');
      setValuationResult(result);
      
      console.log('✓ Setting current step to 2...');
      setCurrentStep(2);
      
      console.log('=== VALUATION CALCULATION COMPLETED SUCCESSFULLY ===');
    } catch (err) {
      console.error('=== VALUATION CALCULATION FAILED ===');
      console.error('Error:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      
      setError(err.message || 'Ein unbekannter Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveValuation = async () => {
    if (!valuationResult || !selectedProperty) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await valuationService.saveValuation(
        selectedProperty.id,
        valuationResult,
        user?.id
      );
      alert('Bewertung erfolgreich gespeichert!');
    } catch (err) {
      console.error('Error saving valuation:', err);
      alert('Fehler beim Speichern der Bewertung');
    }
  };

  const getTrafficLightColor = () => {
    if (!MARKET_COMPARISON_RELEASED) return 'neutral';
    if (!valuationResult || !valuationResult.fair_value_band || !selectedProperty?.purchase_price) return 'yellow';
    
    const purchasePrice = selectedProperty.purchase_price;
    const fairValue = valuationResult.fair_value_band.base;
    const diff = ((fairValue - purchasePrice) / purchasePrice) * 100;
    
    if (diff > 5) return 'green';
    if (diff < -5) return 'red';
    return 'yellow';
  };

  const getTrafficLightLabel = () => {
    if (!valuationResult || !valuationResult.fair_value_band || !selectedProperty?.purchase_price) return 'NEUTRAL';
    
    const purchasePrice = selectedProperty.purchase_price;
    const fairValue = valuationResult.fair_value_band.base;
    const diff = ((fairValue - purchasePrice) / purchasePrice) * 100;
    
    if (!MARKET_COMPARISON_RELEASED) {
      return 'Abweichung zum eingegebenen Kaufpreis – Marktvergleich derzeit nicht ausreichend verifiziert.';
    }
    if (diff > 5) return `UNTERBEWERTET (${diff.toFixed(1)}%)`;
    if (diff < -5) return `ÜBERBEWERTET (${Math.abs(diff).toFixed(1)}%)`;
    return `FAIR BEWERTET (${diff.toFixed(1)}%)`;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black pb-24">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button 
            onClick={() => navigate('/properties')}
            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zu Objekte
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <TrendingUp className="w-8 h-8 mr-3 text-purple-600" />
            Unverbindliche Marktwert-Indikation
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Automatisierte Orientierung auf Basis der verfügbaren Daten; keine Verkehrswertermittlung und kein Gutachten.
          </p>
          <p className="mt-3 max-w-4xl rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900 dark:border-amber-700/80 dark:bg-amber-950/30 dark:text-amber-100">
            Die Anzeige ersetzt keine Besichtigung sowie keine Prüfung von Flächen, Innenzustand, Rechten, Belastungen, Baurecht oder Unterlagen.
          </p>
          
          {/* Progress Stepper */}
          <div className="mt-6 flex items-center gap-4">
            <div className={`flex items-center gap-2 ${currentStep === 1 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                1
              </div>
              <span className="font-medium">Objekt auswählen</span>
            </div>
            <div className="flex-1 h-0.5 bg-gray-300"></div>
            <div className={`flex items-center gap-2 ${currentStep === 2 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                2
              </div>
              <span className="font-medium">Bewertungsergebnis</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {currentStep === 1 && (
          <div className="space-y-6">
            {/* Selection Mode */}
            <div className="bg-white dark:bg-slate-900 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-purple-600" />
                Schritt 1: Objekt auswählen
              </h2>
              
              <div className="space-y-4">
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="selectionMode" 
                      value="existing"
                      checked={selectionMode === 'existing'}
                      onChange={(e) => setSelectionMode(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-gray-700 dark:text-gray-300">Bestandsobjekt bewerten</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="selectionMode" 
                      value="new"
                      checked={selectionMode === 'new'}
                      onChange={(e) => setSelectionMode(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-gray-700 dark:text-gray-300">Neues Objekt bewerten</span>
                  </label>
                </div>

                {selectionMode === 'existing' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Objekt auswählen:
                    </label>
                    <select 
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      onChange={(e) => handlePropertySelect(e.target.value)}
                      value={selectedProperty?.id || ''}
                    >
                      <option value="">-- Bitte wählen --</option>
                      {properties.map(property => (
                        <option key={property.id} value={property.id}>
                          {property.property_name || property.address}
                        </option>
                      ))}
                    </select>
                    
                    {selectedProperty && (
                      <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Adresse:</span>
                            <p className="font-medium text-gray-900 dark:text-white">{selectedProperty.address}</p>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Stadt:</span>
                            <p className="font-medium text-gray-900 dark:text-white">{selectedProperty.city}</p>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Größe:</span>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {selectedProperty.total_area || selectedProperty.total_square_meters || selectedProperty.rentable_area || selectedProperty.size || selectedProperty.living_space || 'N/A'} m²
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Baujahr:</span>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {selectedProperty.year_built || selectedProperty.construction_year || selectedProperty.build_year || 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Adresse:
                      </label>
                      <input 
                        type="text"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        value={newPropertyData.address}
                        onChange={(e) => setNewPropertyData({...newPropertyData, address: e.target.value})}
                        placeholder="Musterstraße 123"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Stadt:
                        </label>
                        <input 
                          type="text"
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          value={newPropertyData.city}
                          onChange={(e) => setNewPropertyData({...newPropertyData, city: e.target.value})}
                          placeholder="München"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          PLZ:
                        </label>
                        <input 
                          type="text"
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          value={newPropertyData.postal_code}
                          onChange={(e) => setNewPropertyData({...newPropertyData, postal_code: e.target.value})}
                          placeholder="80331"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Größe (m²):
                        </label>
                        <input 
                          type="number"
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          value={newPropertyData.size}
                          onChange={(e) => setNewPropertyData({...newPropertyData, size: e.target.value})}
                          placeholder="150"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Baujahr:
                        </label>
                        <input 
                          type="number"
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          value={newPropertyData.build_year}
                          onChange={(e) => setNewPropertyData({...newPropertyData, build_year: e.target.value})}
                          placeholder="2000"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-red-900 dark:text-red-100">Fehler bei der Bewertung</h3>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Calculate Button */}
            <div className="flex justify-end">
              <button
                onClick={handleCalculateValuation}
                disabled={loading}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Berechne...
                  </>
                ) : (
                  <>
                    <TrendingUp className="w-5 h-5" />
                    Bewertung berechnen
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && valuationResult && (
          <div className="space-y-6">
            {/* Error Display in Step 2 - Show if valuation failed */}
            {!valuationResult.success && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">
                      Bewertung konnte nicht durchgeführt werden
                    </h3>
                    <p className="text-red-700 dark:text-red-300 mb-4">
                      {valuationResult.error || 'Ein unbekannter Fehler ist aufgetreten'}
                    </p>
                    
                    {valuationResult.errors && valuationResult.errors.length > 0 && (
                      <div className="mb-4">
                        <p className="font-medium text-red-900 dark:text-red-100 mb-2">Details:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-red-700 dark:text-red-300">
                          {valuationResult.errors.map((err, idx) => (
                            <li key={idx}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {valuationResult.warnings && valuationResult.warnings.length > 0 && (
                      <div className="mb-4">
                        <p className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">Warnungen:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700 dark:text-yellow-300">
                          {valuationResult.warnings.map((warn, idx) => (
                            <li key={idx}>{warn}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <button
                      onClick={() => {
                        setCurrentStep(1);
                        setValuationResult(null);
                        setError(null);
                      }}
                      className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                    >
                      Zurück zur Objektauswahl
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Success Display - Only show if valuation succeeded */}
            {valuationResult.success && valuationResult.fair_value_band && (
              <>
                {/* Fair Value Band */}
                <FairValueBand valuationResult={valuationResult} />
                
                {/* Traffic Light */}
                {selectedProperty?.purchase_price && (
                  <TrafficLight 
                    purchasePrice={selectedProperty.purchase_price}
                    fairValue={valuationResult.fair_value_band.base}
                    color={getTrafficLightColor()}
                    label={getTrafficLightLabel()}
                  />
                )}
                
                {/* Confidence Explainer */}
                {valuationResult.confidence_details && (
                  <ConfidenceExplainer confidenceDetails={valuationResult.confidence_details} />
                )}
                
                {/* Valuation Methods */}
                {valuationResult.methods && valuationResult.weights && (
                  <ValuationMethods methods={valuationResult.methods} weights={valuationResult.weights} />
                )}
                
                {/* INTERNAL-COMPARABLE-ADAPTER-1: interne Faktenquelle */}
                {valuationResult.internal_facts && (
                  <InternalFactsPanel facts={valuationResult.internal_facts} provenance={valuationResult.value_provenance} />
                )}
                {/* Market Comparison */}
                {valuationResult.methods?.comparable_sales?.details?.comparables && (
                  <MarketComparison 
                    comparables={valuationResult.methods.comparable_sales.details.comparables}
                  />
                )}
                
                {/* Data Lineage */}
                {valuationResult.data_lineage && (
                  <DataLineage lineage={valuationResult.data_lineage} />
                )}
                
                {/* Calculation Transparency */}
                <CalculationTransparency valuationResult={valuationResult} />
              </>
            )}
          </div>
        )}
      </div>

      {/* Sticky Action Bar */}
      {currentStep === 2 && valuationResult && valuationResult.success && (
        <StickyActionBar 
          onSave={handleSaveValuation}
          onNewValuation={() => {
            setCurrentStep(1);
            setValuationResult(null);
            setSelectedProperty(null);
            setError(null);
          }}
        />
      )}
    </div>
  );
}

// Sub-Components

function FairValueBand({ valuationResult }) {
  // Safe access with fallbacks
  const conservative = valuationResult?.fair_value_band?.conservative || 0;
  const base = valuationResult?.fair_value_band?.base || 0;
  const aggressive = valuationResult?.fair_value_band?.aggressive || 0;
  
  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
        <TrendingUp className="w-5 h-5 mr-2 text-purple-600" />
        {MARKET_COMPARISON_RELEASED ? 'Fair Value Band' : 'Modellband'}
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Modellband auf Basis systematischer Modellannahmen (±10 %) – keine empirisch kalibrierte Unsicherheit.
      </p>
      
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Conservative</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {conservative.toLocaleString('de-DE')} €
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">-10%</p>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Base</p>
          <p className="text-3xl font-bold text-purple-600">
            {base.toLocaleString('de-DE')} €
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Basis</p>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Aggressive</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {aggressive.toLocaleString('de-DE')} €
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">+10%</p>
        </div>
      </div>
      
      {/* Visual Band */}
      <div className="relative h-12 bg-gradient-to-r from-red-200 via-yellow-200 to-green-200 dark:from-red-900/30 dark:via-yellow-900/30 dark:to-green-900/30 rounded-lg">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-purple-600 rounded-full border-4 border-white shadow-lg"></div>
        <div className="absolute -bottom-6 left-0 text-xs text-gray-600 dark:text-gray-400">
          {conservative.toLocaleString('de-DE')} €
        </div>
        <div className="absolute -bottom-6 right-0 text-xs text-gray-600 dark:text-gray-400">
          {aggressive.toLocaleString('de-DE')} €
        </div>
      </div>
      
      <div className="mt-8">
        <ConfidenceBar score={valuationResult?.confidence || 0} />
      </div>
    </div>
  );
}

function ConfidenceBar() {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/60">
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Datenbasis</span>
        <span className="rounded-full bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-100">siehe Datenbasis-Hinweise</span>
      </div>
      <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-400">
        Die Datenbasis beschreibt ausschließlich die dokumentierte Informationslage. Sie ist keine prozentuale Sicherheit und keine Aussage über die Genauigkeit der Marktwert-Indikation.
      </p>
    </div>
  );
}

function TrafficLight({ purchasePrice, fairValue, color, label }) {
  const diff = fairValue - purchasePrice;
  
  const colorClasses = {
    neutral: 'bg-slate-100 dark:bg-slate-800/60 border-slate-400',
    green: 'bg-green-100 dark:bg-green-900/20 border-green-500',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/20 border-yellow-500',
    red: 'bg-red-100 dark:bg-red-900/20 border-red-500'
  };
  
  const lightColors = {
    neutral: 'bg-slate-500 dark:bg-slate-600',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500'
  };
  
  return (
    <div className={`rounded-lg shadow p-6 border-2 ${colorClasses[color]}`}>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
        <Target className="w-5 h-5 mr-2 text-purple-600" />
        Kaufpreis-Bewertung
      </h2>
      
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Kaufpreis</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {purchasePrice.toLocaleString('de-DE')} €
          </p>
        </div>
        
        <div className={`w-16 h-16 rounded-full ${lightColors[color]} flex items-center justify-center`}>
          <span className="text-white font-bold text-2xl">
            {color === 'green' ? '✓' : color === 'red' ? '✗' : color === 'neutral' ? 'ℹ' : '~'}
          </span>
        </div>
        
        <div className="text-right">
          <p className="text-sm text-gray-600 dark:text-gray-400">{MARKET_COMPARISON_RELEASED ? 'Fair Value' : 'Modellwert'}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {fairValue.toLocaleString('de-DE')} €
          </p>
        </div>
      </div>
      
      <div className="mt-4 text-center">
        <p className="text-lg font-bold text-gray-900 dark:text-white">{label}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Differenz: {diff.toLocaleString('de-DE')} €
        </p>
      </div>
    </div>
  );
}

function ConfidenceExplainer({ confidenceDetails }) {
  const factors = Array.isArray(confidenceDetails?.factors) ? confidenceDetails.factors : [];
  const hasNegative = factors.some((factor) => factor?.status === 'negative');
  const hasFactors = factors.length > 0;
  const statusLabel = !hasFactors ? 'unvollständig / Prüfung nötig' : hasNegative ? 'teilweise dokumentiert' : 'vollständig dokumentiert';
  const statusClasses = !hasFactors
    ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-100'
    : hasNegative
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-100'
      : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          <Info className="w-5 h-5 mr-2 text-purple-600" />
          Datenbasis
        </h2>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClasses}`}>{statusLabel}</span>
      </div>
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        Diese Einordnung zeigt nur, welche Angaben im System dokumentiert sind. Sie ist keine Marktwertgarantie und keine prozentuale Confidence.
      </p>
      {hasFactors ? (
        <div className="space-y-2">
          {factors.map((factor, idx) => {
            const documented = factor?.status === 'positive';
            const text = documented ? 'dokumentiert' : factor?.status === 'negative' ? 'nicht dokumentiert' : 'Prüfung nötig';
            const textClass = documented ? 'text-emerald-700 dark:text-emerald-300' : factor?.status === 'negative' ? 'text-rose-700 dark:text-rose-300' : 'text-amber-700 dark:text-amber-300';
            return (
              <div key={idx} className="flex items-center justify-between gap-4 rounded bg-gray-50 p-3 dark:bg-gray-700">
                <span className="text-sm text-gray-700 dark:text-gray-300">{factor?.factor || 'Angabe'}</span>
                <span className={`shrink-0 text-sm font-medium ${textClass}`}>{text}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="rounded bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-700 dark:text-gray-300">Keine ausreichend strukturierte Datenbasis ausgewiesen.</p>
      )}
    </div>
  );
}

function ValuationMethods({ methods, weights }) {
  if (!methods) return null;
  
  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
        <BarChart3 className="w-5 h-5 mr-2 text-purple-600" />
        Bewertungsmethoden
      </h2>
      
      <div className="grid grid-cols-2 gap-4">
        {methods.income_approach && (
          <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded">
            <h3 className="font-medium text-gray-900 dark:text-white mb-2">{MARKET_COMPARISON_RELEASED ? 'Ertragswertverfahren' : 'Szenario auf dokumentierter Mietbasis'}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Methodengrundlage: Ertragswertansatz</p>
            {methods.income_approach.error ? (
              <p className="text-sm text-red-600 dark:text-red-400">{methods.income_approach.error}</p>
            ) : (
              <>
                <p className="text-2xl font-bold text-purple-600">
                  {methods.income_approach.value?.toLocaleString('de-DE')} €
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Gewichtung: {weights?.income ? (weights.income * 100).toFixed(0) : 50}%
                </p>
              </>
            )}
          </div>
        )}
        
        {methods.comparable_sales && (
          <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded">
            <h3 className="font-medium text-gray-900 dark:text-white mb-2">Vergleichswertverfahren</h3>
            {!MARKET_COMPARISON_RELEASED && (
              <p className="text-xs text-amber-700 dark:text-amber-300 mb-2">Nicht als Marktvergleich freigegeben</p>
            )}
            {methods.comparable_sales.error ? (
              methods.comparable_sales.provenance_gap ? (
                <div className="rounded border border-amber-200 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    {methods.comparable_sales.error}
                  </p>
                  {methods.comparable_sales.message && (
                    <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-200/80">
                      {methods.comparable_sales.message}
                    </p>
                  )}
                  {methods.comparable_sales.recommendation && (
                    <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                      {methods.comparable_sales.recommendation}
                    </p>
                  )}
                  {methods.comparable_sales.market_readiness && (
                    <div className="mt-3 border-t border-amber-200/70 pt-2 dark:border-amber-500/20">
                      <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                        Freigabekriterien Marktvergleich
                      </p>
                      {methods.comparable_sales.market_readiness.checklist.map((gate) => (
                        <div key={gate.id} className="mt-1 flex items-center justify-between gap-2 text-xs">
                          <span className="text-gray-700 dark:text-gray-300">{gate.label}</span>
                          <span className={gate.ok
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-amber-700 dark:text-amber-300'}>
                            {gate.ok ? 'erfüllt' : 'offen – ' + gate.detail}
                          </span>
                        </div>
                      ))}
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {methods.comparable_sales.market_readiness.summary}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-red-600 dark:text-red-400">{methods.comparable_sales.error}</p>
              )
            ) : (
              <>
                <p className="text-2xl font-bold text-purple-600">
                  {methods.comparable_sales.value?.toLocaleString('de-DE')} €
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Gewichtung: {weights?.comparable ? (weights.comparable * 100).toFixed(0) : 50}%
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function InternalFactsPanel({ facts, provenance }) {
  if (!facts) return null;
  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg shadow p-6 mt-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Interne Faktenquelle (kein Marktvergleich)</h2>
      <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">
        Eigener Objekt- und Mietbestand aus Clara360 – reale interne Belege, bewusst getrennt vom Marktvergleich. {facts.adapter_version}
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded">
          <p className="text-xs text-gray-600 dark:text-gray-400">Verifizierte Fälle</p>
          <p className="text-lg font-bold text-green-600 dark:text-green-400">{facts.internal_verified}</p>
        </div>
        <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded">
          <p className="text-xs text-gray-600 dark:text-gray-400">Dokumentiert (unvollständig)</p>
          <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{facts.internal_documented}</p>
        </div>
        <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded">
          <p className="text-xs text-gray-600 dark:text-gray-400">Ausgeschlossen (Demo)</p>
          <p className="text-lg font-bold text-gray-500 dark:text-gray-400">{facts.demo_fixture_excluded}</p>
        </div>
        <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded">
          <p className="text-xs text-gray-600 dark:text-gray-400">Verwendbare reale Fälle</p>
          <p className="text-lg font-bold text-purple-600">{facts.usable_real_cases}</p>
        </div>
      </div>
      {facts.rent_layer && (
        <p className="text-xs text-gray-600 dark:text-gray-400">
          Mietbestand: {facts.rent_layer.units} Einheiten · {facts.rent_layer.tenants} Mieter · {facts.rent_layer.rent_payments} Mietzahlungen – Faktenschicht Ertragswert, keine Marktpreise.
        </p>
      )}
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        Lokale Fälle (Ort des Objekts): {facts.local_cases} · Dubletten ausgeschlossen: {facts.duplicates_excluded} · {facts.policy}
      </p>
      {provenance && (
        <div className="mt-4 border-t border-gray-200 dark:border-slate-700 pt-3">
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Datenherkunft je Zahl (maschinenlesbar)</p>
          {provenance.map((p) => (
            <div key={p.id} data-provenance-id={p.id} className="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs">
              <span className="text-gray-700 dark:text-gray-300">{p.label}</span>
              <span className={p.allowed ? 'text-green-600 dark:text-green-400' : 'text-amber-700 dark:text-amber-300'}>
                {p.source_name} · {p.price_type} · {p.allowed ? 'verwendbar' : 'gesperrt'}: {p.allowed_use}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function MarketComparison({ comparables }) {
  if (!comparables || comparables.length === 0) return null;
  
  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
        <Building className="w-5 h-5 mr-2 text-purple-600" />
        Vergleichsobjekte
      </h2>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-slate-800">
            <tr>
              <th className="px-4 py-2 text-left text-gray-700 dark:text-gray-300">Adresse</th>
              <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">Preis</th>
              <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">€/m²</th>
              <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">Gewichtung</th>
            </tr>
          </thead>
          <tbody>
            {comparables.map((comp, idx) => (
              <tr key={idx} className="border-t border-gray-200 dark:border-gray-700">
                <td className="px-4 py-2 text-gray-900 dark:text-white">{comp.address || 'N/A'}</td>
                <td className="px-4 py-2 text-right text-gray-900 dark:text-white">
                  {comp.price ? comp.price.toLocaleString('de-DE') + ' €' : 'N/A'}
                </td>
                <td className="px-4 py-2 text-right text-gray-900 dark:text-white">
                  {comp.price_per_sqm ? comp.price_per_sqm.toLocaleString('de-DE') + ' €' : 'N/A'}
                </td>
                <td className="px-4 py-2 text-right text-gray-900 dark:text-white">
                  {comp.weight ? (comp.weight * 100).toFixed(0) + '%' : '100%'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatSource(source) {
  if (typeof source === 'string' && source.trim()) return source.trim();
  if (!source || typeof source !== 'object') return '';
  const label = source.label || source.name || source.source || source.provider || source.type;
  const asOf = source.as_of || source.asOf || source.timestamp || source.date;
  if (!label) return '';
  if (!asOf) return String(label);
  const parsed = new Date(asOf);
  const dateLabel = Number.isNaN(parsed.getTime()) ? String(asOf) : parsed.toLocaleDateString('de-DE');
  return `${label} · Stand ${dateLabel}`;
}

function formatSources(sources) {
  if (!Array.isArray(sources)) return 'Quelle nicht ausgewiesen';
  const items = sources.map(formatSource).filter(Boolean);
  return items.length ? items.join(' · ') : 'Quelle nicht ausgewiesen';
}

function DataLineage({ lineage }) {
  if (!lineage) return null;
  
  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
        <Info className="w-5 h-5 mr-2 text-purple-600" />
        Datenherkunft
      </h2>
      
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600 dark:text-gray-400">Zeitstempel:</span>
          <span className="text-gray-900 dark:text-white font-medium">
            {lineage.timestamp ? new Date(lineage.timestamp).toLocaleString('de-DE') : 'N/A'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600 dark:text-gray-400">Objekt:</span>
          <span className="text-gray-900 dark:text-white font-medium">
            {lineage.property_name || lineage.property_id || 'N/A'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600 dark:text-gray-400">Methoden:</span>
          <span className="text-gray-900 dark:text-white font-medium">
            {lineage.methods_used ? lineage.methods_used.join(', ') : 'N/A'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600 dark:text-gray-400">Datenquellen:</span>
          <span className="text-gray-900 dark:text-white font-medium">
            {formatSources(lineage.sources)}
          </span>
        </div>
      </div>
    </div>
  );
}

function StickyActionBar({ onSave, onNewValuation }) {
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-gray-700 shadow-lg z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <button
            onClick={onNewValuation}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Neue Bewertung
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onSave}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Speichern
            </button>
            <span className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 dark:border-slate-600 dark:text-slate-300">
              Report-Export noch nicht verfügbar
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

